/** @format */
import { useEffect, useState, use, Suspense, useEffectEvent, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import './App.css'

import { getCookie, setCookie } from '@/shared/lib/formatter'
import { storage } from '@/shared/storage'
import { dayjs, getAntdLocale, getLocale } from '@/shared/lib/dayjs'
import { ConfigProvider } from 'antd'
import type { Locale } from 'antd/es/locale'
import { useLanguage } from '@/entities/language/model'
import { useTheme } from '@/entities/theme/model'
import { useUpdateCurrentPath, useHandleNavigation, useUpdateChecker } from '@/shared/lib/hooks'
import DynamicLayout from '@/widgets/dynamic-layout'
import { TransactionModalRoot } from '@/widgets/transaction-modal-root'
import { AttachInvoiceModalRoot } from '@/widgets/attach-invoice-modal-root'
import { darkTheme } from './theme'

import { initializeFirebase, logAnalyticsEvent } from './firebase-config'
import { Toast } from '@/shared/ui/organisms/toast'
import NotificationHandler from './app/firebase-notification'
import { LogoPreLoader } from '@/shared/ui/icons'

interface AntdLocaleProviderProps {
  promise: Promise<Locale | null>
  children: ReactNode
}

function AntdLocaleProvider({ promise, children }: AntdLocaleProviderProps) {
  const locale = use(promise)
  return <ConfigProvider locale={locale || undefined}>{children}</ConfigProvider>
}

/**
 * Main App component
 * Handles Firebase initialization, theme management, and locale configuration
 * @returns {JSX.Element} The main application component
 */
function App() {
  const location = useLocation()
  const { lang } = useLanguage()
  const { pathname } = location
  const { theme, setTheme } = useTheme()
  let langCookies = getCookie('lang') ?? 'uzCYR'
  useUpdateCurrentPath()
  useHandleNavigation()
  useUpdateChecker()

  // React 19.2: useEffectEvent for non-reactive callback
  // This callback doesn't need to re-run when pathname/lang changes
  const onFirebaseInitialized = useEffectEvent(() => {
    // pathname and lang are always fresh, but don't cause re-initialization
    logAnalyticsEvent('app_loaded', {
      path: pathname,
      language: lang || langCookies || 'uzCYR',
    })
  })

  // React 19.2: Firebase initialization with useEffectEvent
  useEffect(() => {
    let isMounted = true
    let abortController = new AbortController()

    const setupFirebase = async () => {
      try {
        const result = await initializeFirebase()

        // Only proceed if component is still mounted
        if (!isMounted || abortController.signal.aborted) return

        if (result.success) {
          onFirebaseInitialized()
        }
      } catch (error: unknown) {
        if (isMounted && !abortController.signal.aborted) {
          Toast({
            type: 'error',
            message: `Failed to initialize Firebase: ${
              error instanceof Error ? error.message : 'Unknown error'
            }`,
          })
        }
      }
    }

    setupFirebase()

    // React 19.2: Proper cleanup to prevent memory leaks
    return () => {
      isMounted = false
      abortController.abort()
    }
  }, [onFirebaseInitialized])

  // React 19.2: use() hook for Ant Design locale Promise
  // use() hook cannot be called inside try/catch - must be at top level
  // Create a promise that always resolves (never rejects) to avoid use() hook errors
  const [antdLocalePromise, setAntdLocalePromise] = useState(() => {
    // Ensure promise always resolves, never rejects
    return getAntdLocale(lang || langCookies)
      .then((localeModule: { default?: Locale }) => localeModule?.default || null)
      .catch(() => null)
  })

  // Update locale promise when language changes
  useEffect(() => {
    // Ensure promise always resolves, never rejects
    const newPromise = getAntdLocale(lang || langCookies)
      .then((localeModule: { default?: Locale }) => localeModule?.default || null)
      .catch(() => null)
    setAntdLocalePromise(newPromise)
  }, [lang, langCookies])

  // Locale loading is now handled by AntdLocaleProvider component below

  // React 19.2: useEffectEvent for non-reactive callback
  // updateDayjsLocale doesn't need to re-run when other dependencies change
  const updateDayjsLocale = useEffectEvent(() => {
    // lang and langCookies are always fresh
    const dayjsLocale = getLocale(lang || langCookies)
    dayjs.locale(dayjsLocale)
  })

  // React 19.2: useEffectEvent for theme update
  // updateTheme doesn't need to re-run when other dependencies change
  const updateTheme = useEffectEvent(() => {
    // theme is always fresh
    const currentTheme = theme
    if (typeof currentTheme !== 'object') {
      setTheme(darkTheme)
      setCookie('theme', JSON.stringify(darkTheme))
      setCookie('themeName', darkTheme.themeName)
      storage.prefs.setTheme(darkTheme.themeName)
    } else {
      setTheme(currentTheme)
      setCookie('theme', JSON.stringify(currentTheme))
      setCookie('themeName', currentTheme.themeName ?? darkTheme.themeName)
      storage.prefs.setTheme(currentTheme.themeName ?? darkTheme.themeName)
    }
  })

  // Update Day.js locale when language changes
  useEffect(() => {
    updateDayjsLocale()
  }, [lang, langCookies, updateDayjsLocale])

  // Update theme when it changes
  useEffect(() => {
    updateTheme()
  }, [theme, updateTheme])

  return (
    <Suspense
      fallback={
        <div className='flex items-center justify-center w-full h-screen'>
          <LogoPreLoader size='large' />
        </div>
      }
    >
      <div className='min-h-screen bg-(--main-page-bg)'>
        {/* Use separate component to safely handle use() hook */}
        <AntdLocaleProvider promise={antdLocalePromise}>
          <NotificationHandler />
          <DynamicLayout />
          {/* Global mount for the Income/Expense modal — any feature can open
              it via `openTransactionCreate` / `openTransactionEdit` from
              `@/shared/stores`. See `transaction-modal-store.ts` JSDoc. */}
          <TransactionModalRoot />
          {/* Global mount for the Attach-Invoice modal — any feature can open
              it via `openAttachInvoice` from `@/shared/stores`. Same Modal
              Manager pattern as TransactionModalRoot. */}
          <AttachInvoiceModalRoot />
        </AntdLocaleProvider>
      </div>
    </Suspense>
  )
}

export default App
